import { browseColumnSuperUserClick, changeFilter, getRequiredBrowseData, resetViewAndFilter, updateQuickFilter } from "#skyward_browse";
import { getAllBrowseIds, getBrowseById, getContainingBrowse } from "#skyward_browseList";
import { clearValue, getOriginalValue, getValue, setValue } from "#skyward_controls";
import { CancellationError } from "#skyward_error";
import { virtualDir } from "#skyward_global";
import { DEFAULT_CLOSE_BUTTON, getOpenModalLayers, showDialog, showInformationBubble } from "#skyward_modalLayer";
import { registerBrowseResizeCallback, scrollbarHeight } from "#skyward_window";
import { createCache } from "./skyward.base.extensions";
import { formatServerDate, isDate } from "./skyward.base.time";
import { getPreferences, setPreferences } from "./skyward.ui.browsePreferences";
import { manageButtonClick } from "./skyward.ui.buttonManager";
import { isDirty, setUpChangeTracking } from "./skyward.ui.changeTracking";
import { isEmpty as dmseIsEmpty } from "./skyward.ui.distinctMultiSelectEEL";
import { registerEvents } from "./skyward.ui.events";
import { getWidthsAndSetTwoLineMode } from "./skyward.ui.fieldset";
import { swapSvg } from "./skyward.ui.svg";
import { displayValidationMessages, ValidationData } from "./skyward.ui.validation";
import { Browse } from "./types";

/**
 * Object that defines the parameters needed to build a Filter Maintenance Dialog.
 */
interface DialogOptions
{
    browseId: string; // The browse's client-side id, as found in skyward.browse.browseList
    browseFilterId?: number; // The ID of the BrowseFilter to be operated upon.
    action?: string; // One of "new" to add a new filter, "clone" to clone, or "edit" to edit
    filterData?: string; // Filter data to pass in
    browseOverride?: BrowseOverride;
}

export interface BrowseOverride
{
    isWidget: boolean;
    browseId: string;
    routeHash: string;
    requestDataHash: string;
    pageModule: string;
    pageObject: string;
    pageScreen: string;
    routeModule: string;
    routeObject: string;
    browseName: string;
    dataObject: string;
    dataModule: string;
    selectedBrowseFilterId: number;
    hideUserFilters?: boolean;
    hideActionButtons?: boolean;
    defaultBrowseFilterGuids?: string[];
    excludeNonDefaultBrowseFilters?: boolean;
    onFilterChanged?: (browseFilterID?: number, browseFilterDisplayName?: string) => void;
    additionalFilterFields: Record<string, string>;
    queryParameterData: string;
    queryParameterDataHash: string;
    [key: string]: unknown;
}

export interface Row
{
    FilterType: string | null;
    RangeLow?: Date | string;
    RangeHigh?: Date | string;
    TrueOnly?: boolean | null;
    DmsJson?: string;
}

interface FilterInstance
{
    preInit: () => void;
    init: () => void;
    apply: () => JQuery.Promise<any>;
    reset: () => JQuery.Promise<any>;
    clear: () => void;
    undo: () => void;
    toggle: () => void;
    resize: () => void;
}

const _fieldControlSelector = ".js-browseFilterFieldControl [data-original-value]";

const actionEvents =
{
    initLogicalCheckbox,
    openMaintenanceDialog: openFilterMaintenanceDialogButtonHandler,
    initQuickFilter: function (this: HTMLElement) { getQuickFilterHandlerFromContext(this).init(); },
    quickFilterClear: function (this: HTMLElement) { getQuickFilterHandlerFromContext(this).clear(); },
    quickFilterUndo: function (this: HTMLElement) { getQuickFilterHandlerFromContext(this).undo(); },
    quickFilterApply: function (this: HTMLElement) { getQuickFilterHandlerFromContext(this).apply(); },
    quickFilterReset: function (this: HTMLElement) { getQuickFilterHandlerFromContext(this).reset(); },
    quickFilterToggle: function (this: HTMLElement) { getQuickFilterHandlerFromContext(this).toggle(); }
};

registerEvents("browseFilters", actionEvents);

export const events = Object.freeze(
    {
        //  Triggered when a filter is changed via the filter maintenance dialog or quickFilter
        //  Includes extra parameters:
        //      browse: JQuery containing the browse element that has had its filter changed
        filterChanged: "browseFilters_filterChanged"
    });

// Showing/hiding the quick filter after any layout has been performed on the browse incurs a delay of up
// to 200ms to reflow it.  This method is called early in skyward.browse.registerBrowse() in order to avoid
// the delay.  While it would be safest to run this before the browse is even attached to the dom, parsing
// and then attaching browses is slow in IE, compared to adding them at their destination using innerHTML.
export function preInitQuickFilter(browse: Browse)
{
    const quickFilterHandler = getQuickFilterHandler(browse);
    quickFilterHandler.preInit();
}

/**
 * Opens the Filter Maintenance Dialog
 * @param options (required) Options to build the dialog.
 * @returns Deferred that resolves once the modal closes.
 *      Consider using the skyward.browseFilters.events.filterChanged event if you're interested in the status of the filter.
 */
export function openFilterMaintenanceDialog(options: DialogOptions)
{
    return openFilterMaintenanceDialogHelper(options).done(triggerFilterChangedEvent);

    function triggerFilterChangedEvent(response: Record<string, unknown>)
    {
        if (options.browseOverride)
        {
            options.browseOverride.onFilterChanged?.(response.id as number, response.browseFilterDisplayName as string);
        }
        else
        {
            const browse = getBrowseById(options.browseId)!.browse!;
            browse.trigger(events.filterChanged, { browse: browse });
        }
    }
}

registerBrowseResizeCallback(resizeAllQuickFilters);

function initLogicalCheckbox(context: HTMLElement)
{
    const logicalControl = $(context);

    const toggleLogicalCheckbox = function (this: HTMLElement)
    {
        const clickedCheckbox = $(this);
        if (clickedCheckbox.is(":checked"))
        {
            clickedCheckbox.closest(".js-browseFilterFieldControl").find(":checkbox:checked").not(clickedCheckbox).prop("checked", false).trigger("dirty");
        }
    };
    logicalControl.find(":checkbox").on("change", toggleLogicalCheckbox);
}

function openFilterMaintenanceDialogButtonHandler(this: HTMLElement)
{
    const modifyFilterButton = $(this);
    const buttonData = modifyFilterButton.data() as DialogOptions;

    openFilterMaintenanceDialog(buttonData);
}

/**
  * @returns A Promise that will succeed when the filter is saved, and fail if the dialog is closed without saving.
  */
function openFilterMaintenanceDialogHelper(options: DialogOptions): JQuery.Promise<any>
{
    const browseId = options.browseId;
    const browseFilterId = options.browseFilterId;
    const action = options.action;
    let browse: Browse | BrowseOverride;
    let browseData: any;

    if (options.browseOverride)
    {
        browse = browseData = options.browseOverride;
    }
    else
    {
        browse = getBrowseById(browseId)!;
        browseData = getRequiredBrowseData(browse);
    }

    let title;
    const savePromise = $.Deferred();

    switch (action)
    {
        case "new":
            title = "Add Filter";
            break;
        case "clone":
            title = "Clone Filter";
            break;
        default:
            title = "Edit Filter";
            break;
    }

    const dialogOptions =
    {
        draggable: true,
        closeOnClick: false,
        afterClose: function () { savePromise.reject(new CancellationError("closed")); }
    };

    const dialogData =
    {
        maintentanceMode: action,
        browseFilterId: browseFilterId,
        filterData: options.filterData,
        hideUserFilters: options.browseOverride?.hideUserFilters ?? false,
        hideActionButtons: options.browseOverride?.hideActionButtons ?? false,
        additionalFilterFields: options.browseOverride?.additionalFilterFields ? JSON.stringify(options.browseOverride.additionalFilterFields) : null,
        excludeNonDefaultBrowseFilters: options.browseOverride?.excludeNonDefaultBrowseFilters,
        selectedBrowseFilterId: options.browseOverride?.selectedBrowseFilterId,
    };

    const filterMaintenanceDialog = $.ajax(
        {
            url: `${virtualDir}${browse.routeModule}/${browse.routeObject}/GetBrowseFilterMaintenanceScreen`,
            type: "POST",
            dataType: "viewparthtml",
            activityIndicator: { hideGlass: true },
            data: $.extend({}, browseData, dialogData)
        }).promise();

    const dialog = showInformationBubble(title, filterMaintenanceDialog, [], dialogOptions);

    //remove scrollbars from the content area to fix a rendering issue in IE
    dialog.content.css("overflow", "hidden");

    dialog.content.on("contentLoaded", function (this: HTMLElement)
    {
        const popupContent = $(this);
        const filterMaintenanceContainer = popupContent.find(".js-filterMaintenance");
        const popupData = filterMaintenanceContainer.data();

        const superUserButtons = filterMaintenanceContainer.find(".browseColumnSuperUser");
        superUserButtons.on("click", browseColumnSuperUserClick);
        const infoButtons = filterMaintenanceContainer.find(".browseColumnInfo");
        infoButtons.on("click", browseColumnInfoClick);

        popupContent.find(".js-filterMaintenance__cancel").on("click", dialog.close as () => void);

        const saveChanges = function (this: HTMLElement)
        {
            const saveButton = $(this);
            const textbox = popupContent.find(".js-filterMaintenance__filter_name");
            const buttonData = saveButton.data();
            const saveAction = buttonData.action === "saveasnew" ? "BrowseFilterMaintenanceSaveAsNew" : "BrowseFilterMaintenanceSave";
            const data =
            {
                name: textbox.val(),
                routeHash: browse.routeHash,
                browseFilterId: popupData.browsefilterid,
                browseFilterId_hash: popupData.browsefilterid_hash,
                filterData: serializeFilterMaintenanceData(),
                isWidget: options.browseOverride?.isWidget ?? false
            };

            const postData = $.extend({}, browseData, data);
            if (postData.defaultBrowseFilterGuids)
            {
                postData.defaultBrowseFilterGuids = JSON.stringify(postData.defaultBrowseFilterGuids);
            }
            clearErrorMessages();

            return $.ajax(
                {
                    url: `${virtualDir}skysys/filter/${saveAction}`,
                    type: "POST",
                    dataType: "JSON",
                    data: postData,
                    activityIndicator: { hideGlass: true },
                    success: function (responseData)
                    {
                        if (responseData.success)
                        {
                            savePromise.resolve(responseData);
                            dialog.close();
                            changeFilter(browseId, { browseFilterId: responseData.id });
                        }
                        else
                        {
                            displayErrorMessages(responseData);
                        }
                    }
                }).promise();

            function displayErrorMessages(data: ValidationData | null)
            {
                displayValidationMessages(data, popupContent, popupContent.find(".filterMaintenance__header .help"));
            }

            function clearErrorMessages()
            {
                displayErrorMessages(null);
            }

            function serializeFilterMaintenanceData()
            {
                return serializeFilterRows(popupContent.find(".js-filterMaintenance__filterRow"), function (row)
                {
                    return ((row.find(".js-filterMaintenance__filterTypeRadio :radio:checked").val() || "none") as string).toLowerCase();
                });
            }
        };

        popupContent.find(".js-filterMaintenance__save").on("click", manageButtonClick(saveChanges));

        popupContent.find(".viewAndFilter.reset").on("click", async function ()
        {
            const data = $(this).data();

            await resetViewAndFilter(data.browseViewId, data.browseFilterId, data.browseName);

            // We need to close all modals here in case this was opened from the column options modal.
            // If it was opened from column options, the browse will refresh and the options modal will still be up, inappropriately.
            getOpenModalLayers().each(function (this: HTMLElement)
            {
                $(this).data("layer").close();
            });
        });

        const toggleFilterTypeRadio = function (this: HTMLElement)
        {
            const filterTypeRadio = $(this);
            const filterTypeLabel = filterTypeRadio.closest(".js-filterMaintenance__filterTypeRadio");
            const filterRow = filterTypeLabel.closest(".js-filterMaintenance__filterRow");
            const selectedType = filterTypeLabel.data("selection-type");
            const rangeField = filterRow.find(".js-filterMaintenance__filterField--range");
            const selectionField = filterRow.find(".js-filterMaintenance__filterField--selection");

            rangeField.toggle(selectedType === "range");
            selectionField.toggle(selectedType === "selection");
        };
        popupContent.find(".js-filterMaintenance__filterTypeRadio :radio").on("change", toggleFilterTypeRadio);

        const clearAllButton = popupContent.find(".js-filterMaintenance__clearAll");

        const nonEmptyFields = new Set<HTMLElement>();

        const updateNonEmptyFieldsSet = function (control: HTMLInputElement)
        {
            if (controlIsEmpty(control))
            {
                nonEmptyFields.delete(control);
            }
            else
            {
                nonEmptyFields.add(control);
            }

            function controlIsEmpty(control: HTMLInputElement)
            {
                const inputType = control.getAttribute("type");
                if (inputType === "checkbox")
                {
                    return !control.checked;
                }
                if (control.classList.contains("dmsBackingData"))
                {
                    return dmseIsEmpty($(control));
                }
                if (control.classList.contains("relativeDate"))
                {
                    return !$(control).data("relativeDate");
                }
                return !control.value;
            }
        };
        const toggleClearAllButton = function ()
        {
            clearAllButton.css("visibility", nonEmptyFields.size > 0 ? "" : "hidden");
        };
        popupContent.on("change dirty keyup", _fieldControlSelector, function ()
        {
            updateNonEmptyFieldsSet(this);
            toggleClearAllButton();
        });
        clearAllButton.on("click", function ()
        {
            nonEmptyFields.forEach(function (elem)
            {
                clearValue(elem);
            });
        });

        popupContent.find(_fieldControlSelector).each(function (this: HTMLElement) { updateNonEmptyFieldsSet(this as HTMLInputElement); });
        toggleClearAllButton();
    });

    return savePromise.promise();
}

function serializeFilterRows(rows: JQuery<HTMLElement>, rowTypeCallback: ((field: JQuery<HTMLElement>) => string | null))
{
    const allRowsData: Record<string, any> = {};
    rows.each(function ()
    {
        const field = $(this);
        const filterType = rowTypeCallback(field);
        allRowsData[field.data("fieldpath")] = serializeFilterRow(field, filterType);
    });
    return JSON.stringify(allRowsData);
}

function serializeFilterRow(row: JQuery<HTMLElement>, filterType: string | null)
{
    const rowJson: Row = { FilterType: filterType };

    if (filterType === "range")
    {
        const rangeLowElement = row.find(".js-browseFilterFieldControl__rangeLow");
        const rangeHighElement = row.find(".js-browseFilterFieldControl__rangeHigh");

        rowJson.RangeLow = getValue(rangeLowElement);
        rowJson.RangeHigh = getValue(rangeHighElement);

        if (isDate(rowJson.RangeLow, false))
        {
            rowJson.RangeLow = formatServerDate(rowJson.RangeLow);
        }
        if (isDate(rowJson.RangeHigh, false))
        {
            rowJson.RangeHigh = formatServerDate(rowJson.RangeHigh);
        }
    }
    else if (filterType === "selection")
    {
        const logicalControl = row.find(".js-browseFilterFieldControl--logical").addBack(".js-browseFilterFieldControl--logical").find(":checkbox:checked");
        if (logicalControl.length)
        {
            rowJson.TrueOnly = logicalControl.data("type") === "True";
        }
        else
        {
            const dmsElement = row.find(".dmsBackingData");
            const dmsData = getValue(dmsElement);
            rowJson.DmsJson = JSON.stringify(dmsData);
        }
    }
    return rowJson;
}

function getQuickFilterHandler(browse: Browse, eventContext?: JQuery<HTMLElement>)
{
    const _browse = browse;
    const _eventContext = eventContext ?? _browse.browse;

    const instance: FilterInstance =
    {
        preInit,
        init,
        apply,
        reset,
        clear,
        undo,
        toggle,
        resize
    };

    return instance;

    function preInit()
    {
        if (_browse.quickFilter!.length === 0)
        {
            return;
        }

        const browsePrefs = getPreferences(_browse.id);

        // .animateToggle() and even .toggle() read css
        const openQuickFilter = browsePrefs.quickFilterOpen ?? browse.browse?.data("openQuickFilter") ?? false;
        if (!openQuickFilter)
        {
            _browse.quickFilter![0].style.display = "none";
        }
        setButtonSvg(openQuickFilter);
    }

    function init()
    {
        const quickFilter = _browse.quickFilter!;
        const fieldContainer = quickFilter.find(".js-expandableQuickFilter__fields");
        const saveControls = quickFilter.find(".js-change");
        setUpChangeTracking(fieldContainer, function ()
        {
            saveControls.toggleClass("expandableQuickFilter__control--hidden", !isDirty(fieldContainer));
        });

        // Ensure Quick Filter Controls are not posted in the Data Object Viewer
        const inputs = fieldContainer.find("[name]");
        inputs.addClass("doNotPost");
    }

    function apply()
    {
        return updateQuickFilter(_browse.id, serializeQuickFilter(_browse.quickFilter!))
            .done(triggerFilterChangedEvent)
            .then(restoreQuickFilterScrollOnLoad);

        function restoreQuickFilterScrollOnLoad()
        {
            const oldScroll = _browse.quickFilter!.find(".js-expandableQuickFilter__fields").scrollTop()!;
            $(document.body).one(`browseLoaded.${_browse.id}`, function ()
            {
                _browse.quickFilter!.find(".js-expandableQuickFilter__fields").scrollTop(oldScroll);
            });
        }
    }

    function reset()
    {
        return $.ajax(
            {
                url: `${virtualDir}skysys/browsefilter/reset/${_browse.browseFilterId}`,
                type: "POST",
                success: function (response)
                {
                    changeFilter(_browse.id, { browseFilterId: String(response.id) }).done(triggerFilterChangedEvent);
                }
            }).promise();
    }

    function clear()
    {
        _browse.quickFilter!.find(_fieldControlSelector).each(function (this: HTMLElement)
        {
            clearValue(this);
        });
    }

    function undo()
    {
        _browse.quickFilter!.find(".js-browseFilterFieldControl [data-original-value][data-dirty='true']").each(function (this: HTMLElement)
        {
            setValue(this, getOriginalValue(this));
        });
    }

    function toggle()
    {
        const quickFilter = _browse.quickFilter!;
        const wasOpen = quickFilter.is(":visible");
        quickFilterExpand(!wasOpen);
        const preferences = getPreferences(_browse.id);
        preferences.quickFilterOpen = !wasOpen;
        setPreferences(_browse.id, preferences);

        function quickFilterExpand(open: boolean)
        {
            const currentQuickFilter = _browse.quickFilter!;
            if (currentQuickFilter.length === 0)
            {
                return;
            }
            if (open && !currentQuickFilter.hasClass("js-expandableQuickFilter--empty"))
            {
                const animateOptions =
                {
                    start: function () { quickFilterLayout(currentQuickFilter); }
                };
                currentQuickFilter.animateToggle(animateOptions, open);
            }
            else
            {
                currentQuickFilter.animateToggle(open);
            }

            setButtonSvg(open);
        }
    }

    function triggerFilterChangedEvent()
    {
        _eventContext?.trigger(events.filterChanged, { browse: _browse.browse });
    }

    function setButtonSvg(open: boolean)
    {
        const button = _browse.controls!.find(".js-browseQuickFilterButton");
        button.attr("aria-expanded", open.toString());
        swapSvg(_browse.controls!.find(".js-browseQuickFilterButton svg") as JQuery<SVGElement>, open ? "ChevronUp" : "ChevronDown", "BrowseLevel");
    }

    function serializeQuickFilter(quickFilter: JQuery<HTMLElement>)
    {
        return serializeFilterRows(quickFilter.find(".js-browseFilterFieldControl"), function (field)
        {
            if (field.hasClass("js-browseFilterFieldControl--range"))
            {
                return "range";
            }
            else if (field.hasClass("js-browseFilterFieldControl--selection") || field.hasClass("js-browseFilterFieldControl--logical"))
            {
                return "selection";
            }
            return null;
        });
    }

    /**
     * Update the number of columns shown by the quick filter, to fit the available horizontal space.
     * @param quickFilter The quick filter on which to update the number of columns and line wrapping
    */
    function quickFilterLayout(quickFilter: JQuery<HTMLElement>)
    {
        // This is called during resize, so time is at a premium.
        // $.data() doesn't look at data- attributes, making it much faster than $(elem).data.
        const data = $.data(quickFilter[0]);
        const fieldContainerElem = quickFilter[0].querySelector<HTMLElement>(".js-expandableQuickFilter__fields")!;
        const columnWidths = getColumnWidths(fieldContainerElem, data);

        // do we already have the right number of columns?
        if (data.columns === columnWidths.length)
        {
            return;
        }
        data.columns = columnWidths.length;

        const fieldContainer = $(fieldContainerElem);

        // Make sure labels that wrap to 2+ lines look nice.  This shouldn't affect their width, and doing it after getColumnWidths()
        // ensures we don't invalidate their styles before measuring them.
        if (!data.labelsLaidOut)
        {
            labelLayout(fieldContainer);
            data.labelsLaidOut = true;
        }

        setFakeGridColumns(fieldContainer, columnWidths);

        /** We can't use CSS grid due to https://bugs.webkit.org/show_bug.cgi?id=190845, so this function fakes it by setting each element in
         * a column to the same width.  Each field element is followed by a width: 100% div that we show/hide in order to force wrapping after
         * the last column.
         * @param fieldContainer The field container on which to set grids
         * @param columns The columns' widths
         */
        function setFakeGridColumns(fieldContainer: JQuery<Element>, columns: number[])
        {
            const children = fieldContainer.children();
            let columnIndex = 0;

            for (let childIndex = 0; childIndex < children.length; childIndex += 3)
            {
                // label column
                $(children[childIndex]).css("width", columns[columnIndex]);
                // control column
                $(children[childIndex + 1]).css("width", columns[columnIndex + 1]);
                columnIndex += 2;
                if (columnIndex === columns.length)
                {
                    // This is the last column, show the line-break
                    $(children[childIndex + 2]).css("display", "block");
                    columnIndex = 0;
                }
                else
                {
                    // Hide the line break
                    $(children[childIndex + 2]).css("display", "");
                }
            }
        }

        /** Calculate the maximum number of columns that will fit in the quick filter at its current width
        * @param fieldContainer the quick filter to measure
        * @param data the quick filter's jquery data
        * @returns the columns' widths */
        function getColumnWidths(fieldContainer: Element, data: Record<string, any>): number[]
        {
            const width = fieldContainer.getBoundingClientRect().width - scrollbarHeight;
            if (!data.getColumnWidths)
            {
                const elementSizes = Array.from(fieldContainer.children)
                    .filter(function (elem) { return !elem.classList.contains("expandableQuickFilter__fieldLineBreak"); })
                    .map(function (elem) { return parseFloat($.css(elem, "width")); });

                data.getColumnWidths = createCache(function (fieldColumns: number)
                {
                    return measureTotalColumnWidth(elementSizes, fieldColumns * 2);
                });
            }

            let fieldColumns = 1;
            while (data.getColumnWidths(fieldColumns + 1).total < width)
            {
                fieldColumns += 1;
            }

            return data.getColumnWidths(fieldColumns).widths;

            /**
             * Get the total and per-column widths, for a given number of columns.
             * @param widths The individual fields' widths
             * @param gridColumns The number of columns
             * @returns The columns' widths, for layout, and their sum, for quickly checking if this number of columns will fit
             */
            function measureTotalColumnWidth(widths: number[], gridColumns: number)
            {
                const columnWidths = [];
                let total = 0;
                for (let column = 0; column < gridColumns; column++)
                {
                    let size = widths[column];
                    for (let i = column + gridColumns; i < widths.length; i += gridColumns)
                    {
                        size = Math.max(size, widths[i]);
                    }
                    columnWidths.push(size);
                    total += size;
                }
                return { widths: columnWidths, total: total };
            }
        }

        /**
         * Apply fieldset-like wrapping to quick filter labels
         * @param fieldContainer the quick filter's field container
         */
        function labelLayout(fieldContainer: JQuery<HTMLElement>)
        {
            const labels = fieldContainer.children(".js-expandableQuickFilter__label");
            getWidthsAndSetTwoLineMode(labels);
        }
    }

    function resize()
    {
        const quickFilter = _browse.quickFilter;

        // When we are called during the initial DOMContentLoaded notifyResize, browses may not be loaded yet, and so quickFilter may not exist
        if (quickFilter !== undefined && quickFilter.length > 0 && quickFilter[0].style.display !== "none" && !quickFilter[0].classList.contains("js-expandableQuickFilter--empty"))
        {
            quickFilterLayout(quickFilter);
        }
    }
}

function resizeAllQuickFilters()
{
    // This is much faster than $(".expandableQuickFilter:not(.expandableQuickFilter--empty):visible"), which is important for a resize callback
    getAllBrowseIds().forEach(function (key)
    {
        const browse = getBrowseById(key);
        const quickFilterHandler = getQuickFilterHandler(browse!);
        quickFilterHandler.resize();
    });
}

function getQuickFilterHandlerFromContext(context: HTMLElement)
{
    const browse = getContainingBrowse(context)!;
    return getQuickFilterHandler(browse, $(context));
}

function browseColumnInfoClick(this: HTMLElement | JQuery<HTMLElement>)
{
    const clickedElement = $(this);
    const fieldPath = clickedElement.data().fieldPath ?? "Unable to find field path.";
    showDialog("Field Path", fieldPath, [DEFAULT_CLOSE_BUTTON], { anchorTo: clickedElement });
}